import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Star, Triangle, Ruler, Heart, Award, Hammer } from 'lucide-react';

const content = {
  he: {
    eyebrow: 'יצירה מרכזית',
    title: 'גולדן עוז',
    titleEn: 'Golden Oz',
    location: 'נחל עוז',
    tagline: '"מיקרו מינרל" | חומר, גיאומטריה וזיכרון',
    intro:
      'היצירה "גולדן עוז" הינה מיצב תלת־ממדי המשלב תכנון מתמטי עם חומרים טבעיים מן האדמה, במטרה ליצור מרחב של איזון, הרמוניה וזיכרון.',
    sections: [
      {
        icon: Triangle,
        title: 'הגיאומטריה: בין השלם לשבור',
        body: 'המיצב מורכב משלושה לוחות MDF המתוכננים לפי יחס הזהב (1:1.618) – יחס המבטא איזון והרמוניה טבעית בין חלקי היצירה.',
        bullets: [
          'הלוח הימני: מייצג את אדמת המקום הטבעית.',
          'הלוחות האמצעיים: מייצגים את השאיפה לסדר ואיזון מדויק, בגווני זהוב ואפור אנרגטי.',
          'הלוח השמאלי: כולל שני צבעי אדמה אדומה היוצרים "ריבוע כמעט שלם", חוסר שלמות זה נועד לשבור את השלמות הגיאומטרית לטובת ערך ההנצחה.',
        ],
      },
      {
        icon: Heart,
        title: 'המקצב: 71.0 מ"מ',
        body: 'המידה 71.0 מ"מ (7.10 ס"מ) חוזרת ביצירה כ"פעימה" קבועה: ברווח שבין הלוחות, בנתק שבין משולשי הלוח האמצעי ובעומק הניתוק מן הקיר. מידה זו נבחרה כסמל ליום ה־7.10, לזכר הנופלים הגיבורים מאירועי נחל עוז ומדינת ישראל.',
      },
      {
        icon: Star,
        title: 'החומרים: מינרלים מן האדמה',
        body: 'היצירה עושה שימוש במרקמים מינרליים המקשרים אותנו אל הקרקע והשורשיות:',
        bullets: [
          'אדמת נחל עוז: הלוח הימני מצופה באדמה טבעית שנאספה מהשטח, המבטאת את החיבור העמוק לאדמה.',
          'שלושה משולשים (שיקום): הלוח האמצעי משלב גוונים של בז\', חול זהוב (מינרל יתיר) ואפור־מיקה, המסמלים את איזון ושיקום הקיבוץ.',
          'חרסית אדומה: הלוח השמאלי עשוי גווני חרסית המשקפים את צבע האדמה של אותו היום.',
        ],
      },
    ],
    specsTitle: 'נתונים טכניים',
    specs: [
      { label: 'רוחב כולל', value: '3,213 מ"מ', sub: '(3.213 מטרים)' },
      { label: 'גובה', value: '741.6 מ"מ', sub: '' },
      { label: 'עובי הלוח', value: '20 מ"מ', sub: '' },
      { label: 'רקע', value: 'מינרל מיקה אפור', sub: 'גמר אנרגיה חיובית' },
    ],
    memorial: 'לזכר הנופלים הגיבורים — 7.10',
    creditsTitle: 'הוקרה ותודה',
    creditDonor: { label: 'תרומת היצירה', value: 'שחל חומרים לבניין' },
    creditExecutor: { label: 'ביצוע היצירה', value: 'עומרי איל' },
  },
  en: {
    eyebrow: 'Featured Artwork',
    title: 'Golden Oz',
    titleEn: '',
    location: 'Nahal Oz',
    tagline: '"Micro Mineral" | Material, Geometry & Memory',
    intro:
      'Golden Oz is a three-dimensional installation that combines mathematical design with natural materials from the earth, aiming to create a space of balance, harmony and remembrance.',
    sections: [
      {
        icon: Triangle,
        title: 'Geometry: Between Whole and Broken',
        body: 'The installation consists of three MDF panels designed according to the golden ratio (1:1.618) — a ratio that expresses natural balance and harmony between parts of the work.',
        bullets: [
          'Right panel: represents the natural earth of the site.',
          'Middle panels: represent the aspiration for order and precise balance, in golden and energetic gray tones.',
          'Left panel: includes two red earth colors forming an "almost complete square" — this imperfection is intended to break the geometric wholeness in favor of the commemorative value.',
        ],
      },
      {
        icon: Heart,
        title: 'The Rhythm: 71.0 mm',
        body: 'The measurement 71.0 mm (7.10 cm) recurs throughout the work as a constant "pulse": in the gap between panels, in the separation between triangles of the middle panel, and in the depth of detachment from the wall. This measure was chosen as a symbol of October 7, in memory of the fallen heroes from the events of Nahal Oz and the State of Israel.',
      },
      {
        icon: Star,
        title: 'Materials: Minerals From the Earth',
        body: 'The artwork uses mineral textures that connect us to the ground and rootedness:',
        bullets: [
          'Nahal Oz earth: the right panel is coated with natural earth collected from the site, expressing deep connection to the land.',
          'Three triangles (restoration): the middle panel combines tones of beige, golden sand (Yatir mineral) and mica-gray, symbolizing the balance and rehabilitation of the kibbutz.',
          'Red clay: the left panel is made of clay tones reflecting the color of the earth on that day.',
        ],
      },
    ],
    specsTitle: 'Technical Specifications',
    specs: [
      { label: 'Total width', value: '3,213 mm', sub: '(3.213 m)' },
      { label: 'Height', value: '741.6 mm', sub: '' },
      { label: 'Panel thickness', value: '20 mm', sub: '' },
      { label: 'Background', value: 'Gray Mica Mineral', sub: 'Positive-energy finish' },
    ],
    memorial: 'In memory of the fallen heroes — 7.10',
    creditsTitle: 'Acknowledgements',
    creditDonor: { label: 'Donated by', value: 'Shahal Building Materials' },
    creditExecutor: { label: 'Executed by', value: 'Omri Eyal' },
  },
};

export const GoldenOzPage = () => {
  const { isRTL } = useLanguage();
  const t = isRTL ? content.he : content.en;

  return (
    <section
      id="golden-oz-section"
      data-testid="golden-oz-section"
      className="relative py-12 md:py-20 px-4 overflow-hidden"
      dir={isRTL ? 'rtl' : 'ltr'}
      itemScope
      itemType="https://schema.org/VisualArtwork"
    >
      {/* Hidden SEO meta inside DOM (machine-readable schema fields) */}
      <meta itemProp="name" content="גולדן עוז" />
      <meta itemProp="alternateName" content="Golden Oz" />
      <meta itemProp="artform" content="Mineral Installation Art" />
      <meta itemProp="artMedium" content="MDF, ציפוי מינרלי, מיקרומינרל, אדמת נחל עוז, יתיר, מיקה, חרסית אדומה" />
      <link itemProp="image" href="/images/golden_oz.jpg" />

      {/* Background — warm earth gradient */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#FAF6EE] via-[#F5EBD7] to-[#EFE0C2]" />
      <div className="absolute inset-0 -z-10 opacity-30 mix-blend-multiply texture-noise" />

      {/* Decorative golden triangle accent */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.08, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2 }}
        className="pointer-events-none absolute -top-20 -right-20 w-[420px] h-[420px] -z-10"
      >
        <div
          className="w-full h-full"
          style={{
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            background: 'linear-gradient(180deg, #E5B53A 0%, #D4A84B 100%)',
          }}
        />
      </motion.div>

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-10 md:mb-14"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E5B53A]/15 border border-[#E5B53A]/30 mb-5">
            <Star className="w-3.5 h-3.5 text-[#B8862C] fill-[#E5B53A]" />
            <span className="text-xs font-medium tracking-widest uppercase text-[#8C661F]">
              {t.eyebrow}
            </span>
          </div>

          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3A2E18] mb-3 tracking-tight"
            data-testid="golden-oz-title"
            itemProp="headline"
          >
            {t.title}
            {t.titleEn && (
              <span className="block sm:inline text-[#B8862C] sm:ms-3 mt-1 sm:mt-0">
                — {t.titleEn}
              </span>
            )}
          </h1>

          <p className="text-base sm:text-lg text-[#6B5A3A] mb-2" itemProp="locationCreated">
            {t.location}
          </p>

          <div className="flex items-center justify-center gap-3 my-5">
            <div className="h-px w-12 bg-[#B8862C]/40" />
            <p className="text-sm sm:text-base text-[#8C661F] italic font-medium">
              {t.tagline}
            </p>
            <div className="h-px w-12 bg-[#B8862C]/40" />
          </div>

          <p className="max-w-2xl mx-auto text-base sm:text-lg text-[#4A3E22] leading-relaxed" itemProp="description">
            {t.intro}
          </p>
        </motion.div>

        {/* Image Showcase */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="relative mb-12 md:mb-16"
        >
          <figure className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-[#B8862C]/20 bg-white/40">
            <img
              src="/images/golden_oz.jpg"
              alt={isRTL
                ? 'גולדן עוז (Golden Oz) - מיצב אומנותי תלת-ממדי בציפוי מינרלי מיקרומינרל, לזכר נופלי 7.10 בנחל עוז. שלושה לוחות MDF: אדמת נחל עוז, משולש יתיר זהוב, וחרסית אדומה.'
                : 'Golden Oz - 3D mineral coated art installation commemorating the fallen of October 7 in Nahal Oz. Three MDF panels: Nahal Oz earth, Yatir golden triangle, and red clay.'
              }
              className="w-full h-auto block"
              data-testid="golden-oz-image"
              loading="lazy"
              width="1524"
              height="704"
            />
            <figcaption className="sr-only">
              {isRTL
                ? 'גולדן עוז - יצירת אומנות מינרלית של M\'art במיקרומינרל, הנצחת 7.10, נחל עוז'
                : 'Golden Oz - mineral artwork by M\'art with microMineral coating, 7.10 commemoration, Nahal Oz'}
            </figcaption>
          </figure>
          <p className="text-center text-xs sm:text-sm text-[#8C661F]/80 mt-3 italic">
            {isRTL
              ? 'שלושת הלוחות: אדמת נחל עוז | משולשי השיקום | חרסית אדומה'
              : 'The three panels: Nahal Oz earth | Restoration triangles | Red clay'}
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-8 md:space-y-10 mb-12">
          {t.sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="relative bg-white/60 backdrop-blur-sm rounded-2xl border border-[#B8862C]/15 p-6 sm:p-8 shadow-sm"
                data-testid={`golden-oz-section-${idx}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#E5B53A]/15 border border-[#E5B53A]/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#B8862C]" />
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-[#3A2E18] pt-1.5">
                    {section.title}
                  </h2>
                </div>

                <p className="text-base text-[#4A3E22] leading-relaxed mb-3">
                  {section.body}
                </p>

                {section.bullets && (
                  <ul className="space-y-2.5 mt-4">
                    {section.bullets.map((bullet, bIdx) => (
                      <li
                        key={bIdx}
                        className="flex items-start gap-3 text-sm sm:text-base text-[#4A3E22]"
                      >
                        <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-[#B8862C]" />
                        <span className="leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Technical specs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl bg-gradient-to-br from-[#3A2E18] to-[#5C4823] text-white p-6 sm:p-8 shadow-xl mb-10"
          data-testid="golden-oz-specs"
        >
          <div className="flex items-center gap-3 mb-5">
            <Ruler className="w-5 h-5 text-[#E5B53A]" />
            <h3 className="text-lg sm:text-xl font-bold tracking-wide">
              {t.specsTitle}
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            {t.specs.map((spec, idx) => (
              <div
                key={idx}
                className="text-center sm:text-start border-t border-[#E5B53A]/20 sm:border-t-0 sm:border-s sm:ps-4 pt-3 sm:pt-0 first:border-t-0 first:pt-0"
              >
                <div className="text-xs uppercase tracking-wider text-[#E5B53A]/80 mb-1">
                  {spec.label}
                </div>
                <div className="text-lg sm:text-xl font-bold text-white">
                  {spec.value}
                </div>
                {spec.sub && (
                  <div className="text-xs text-white/60 mt-0.5">{spec.sub}</div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Memorial line */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#3A2E18]/5 border border-[#3A2E18]/15">
            <Heart className="w-4 h-4 text-[#B8862C] fill-[#E5B53A]/30" />
            <span className="text-sm sm:text-base text-[#3A2E18] font-medium tracking-wide">
              {t.memorial}
            </span>
            <Heart className="w-4 h-4 text-[#B8862C] fill-[#E5B53A]/30" />
          </div>
        </motion.div>

        {/* Credits — Acknowledgements */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 max-w-2xl mx-auto"
          data-testid="golden-oz-credits"
        >
          <div className="text-center mb-4">
            <span className="inline-block text-xs uppercase tracking-[0.2em] text-[#8C661F]/80 font-medium">
              {t.creditsTitle}
            </span>
            <div className="mt-1.5 mx-auto w-12 h-px bg-[#B8862C]/40" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Donor */}
            <div
              className="group flex items-start gap-3 bg-white/70 backdrop-blur-sm rounded-xl border border-[#B8862C]/20 p-4 hover:border-[#B8862C]/40 hover:shadow-md transition-all"
              itemProp="sponsor"
              itemScope
              itemType="https://schema.org/Organization"
            >
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#E5B53A]/15 border border-[#E5B53A]/30 flex items-center justify-center">
                <Award className="w-4 h-4 text-[#B8862C]" />
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-[#8C661F]/80 mb-0.5">
                  {t.creditDonor.label}
                </div>
                <div
                  className="text-base font-bold text-[#3A2E18] leading-tight"
                  itemProp="name"
                >
                  {t.creditDonor.value}
                </div>
              </div>
            </div>

            {/* Executor */}
            <div
              className="group flex items-start gap-3 bg-white/70 backdrop-blur-sm rounded-xl border border-[#B8862C]/20 p-4 hover:border-[#B8862C]/40 hover:shadow-md transition-all"
              itemProp="contributor"
              itemScope
              itemType="https://schema.org/Person"
            >
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#E5B53A]/15 border border-[#E5B53A]/30 flex items-center justify-center">
                <Hammer className="w-4 h-4 text-[#B8862C]" />
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-[#8C661F]/80 mb-0.5">
                  {t.creditExecutor.label}
                </div>
                <div
                  className="text-base font-bold text-[#3A2E18] leading-tight"
                  itemProp="name"
                >
                  {t.creditExecutor.value}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* SEO content — visually hidden but crawlable by Google */}
        <div className="sr-only" aria-hidden="true">
          <h2>גולדן עוז (Golden Oz) — אומנות מינרלית בנחל עוז</h2>
          <p>
            גולדן עוז הוא מיצב אומנות תלת־ממדי ייחודי של M'art Mineral (מארט), שעוצב לזכר הנופלים הגיבורים מאירועי 7 באוקטובר בנחל עוז. היצירה משלבת ציפוי מינרלי מסוג מיקרומינרל (Microlit / מיקרוליט) על שלושה לוחות MDF, על פי יחס הזהב המתמטי 1:1.618.
          </p>
          <p>
            <strong>היצירה נתרמה על ידי שחל חומרים לבניין</strong> ו<strong>בוצעה על ידי עומרי איל</strong>. שחל חומרים לבניין הוא תורם נדיב שאפשר את הקמת המיצב לזכר הנופלים. עומרי איל הוא האמן והמבצע שאחראי על הביצוע המקצועי של היצירה בציפויים המינרליים.
          </p>
          <p>
            המיצב משלב חומרים טבעיים מן האדמה: אדמת נחל עוז שנאספה מהשטח עצמו, מינרל יתיר זהוב, מיקה אפורה, וחרסית אדומה. מקצב המידות של 71.0 מ"מ (7.10 ס"מ) חוזר ביצירה כפעימת זיכרון — סמל ליום ה־7.10 ולהנצחת 7.10. רוחב כולל: 3,213 מ"מ, גובה: 741.6 מ"מ, עומק לוח: 20 מ"מ.
          </p>
          <p>
            מילות מפתח: גולדן עוז, Golden Oz, ציפוי מינרלי, מיקרומינרל, Microlit, מיקרוליט, M'art, מארט, נחל עוז, כיכר נחל עוז אומנות, הנצחה 7.10, יום השבעה באוקטובר, מיצב אומנותי, יחס הזהב, ציפוי בטון, ציפוי קלקר, ציפוי הדפסת תלת ממד, חומרי בנייה, עיצוב פנים מינרלי, מיקרוטופינג, גמר מינרלי, חרסית אדומה, מיקה, יתיר, מינרל אדמה, אומנות ישראלית, הנצחה, זיכרון 7.10, שחל חומרים לבניין, שחל בניין, עומרי איל, אמן מינרלי, ביצוע ציפויים מינרליים, תורם, נדבן, אמנים ישראלים.
          </p>
          <p>
            Keywords: Golden Oz, Mineral Coating, MicroMineral, Microlit, M'art, Nahal Oz, October 7 Memorial, 7.10 Commemoration, Mineral Art Installation, Golden Ratio Art, Concrete Coating, Foam Coating, 3D Print Coating, Building Materials, Mineral Finish, Micro-topping, Israeli Memorial Art, Earth Pigments, Yatir mineral, Mica, Red Clay, Shahal Building Materials, Shahal, Omri Eyal, Mineral Artist, Donor, Israeli Artists.
          </p>
        </div>
      </div>
    </section>
  );
};

export default GoldenOzPage;
